<template>
  <div class="flex justify-center items-center h-screen bg-gray-50">
    <div class="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
      <h2 class="text-2xl font-bold text-center text-gray-800">Check your email</h2>
      <p class="text-center text-gray-600">We've sent a 6-digit confirmation code to <span class="font-semibold">{{ email }}</span>. The code expires in 15 minutes.</p>

      <form @submit.prevent="confirmEmail">
        <div class="flex justify-center space-x-2">
          <input
              v-for="(n, index) in codeLength"
              :key="index"
              :ref="el => { if (el) inputs[index] = el }"
              v-model="code[index]"
              type="text"
              maxlength="1"
              class="w-12 h-14 text-center text-2xl font-semibold border-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              @input="handleInput(index, $event)"
              @keydown="handleKeyDown(index, $event)"
          />
        </div>

        <div v-if="errorMessage" class="text-red-500 text-center mt-4">{{ errorMessage }}</div>

        <button type="submit" class="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md transition" :disabled="isConfirming">
          <span v-if="isConfirming">Verifying...</span>
          <span v-else>Verify Account</span>
        </button>
      </form>
      <div class="mt-4 text-center text-sm">
        <router-link to="/login" class="text-blue-500 hover:text-blue-700">Back to Login</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal'

const route = useRoute()
const router = useRouter()
const modal = useModalStore()

const codeLength = 6
const code = ref(Array(codeLength).fill(''))
const inputs = ref([])
const email = ref('')
const errorMessage = ref('')
const isConfirming = ref(false)

onMounted(() => {
  email.value = route.query.email || ''
  if (!email.value) {
    router.push('/signup') // Redirect if no email is provided
  }
  // Focus the first input on page load
  inputs.value[0]?.focus()
})

const handleInput = (index, event) => {
  const value = event.target.value
  if (value.length === 1 && index < codeLength - 1) {
    inputs.value[index + 1]?.focus()
  }
}

const handleKeyDown = (index, event) => {
  if (event.key === 'Backspace' && index > 0 && !code.value[index]) {
    inputs.value[index - 1]?.focus()
  }
}

const confirmEmail = async () => {
  const fullCode = code.value.join('')
  if (fullCode.length !== codeLength) {
    errorMessage.value = 'Please enter the complete 6-digit code.'
    return
  }

  isConfirming.value = true
  errorMessage.value = ''
  try {
    await axios.post('/confirm-email', {
      email: email.value,
      code: fullCode
    })

    await modal.success('Email confirmed successfully! You can now log in.')
    router.push('/login')
  } catch (error) {
    errorMessage.value = error.response?.data || 'An error occurred. Please try again.'
  } finally {
    isConfirming.value = false
  }
}
</script>
